import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { environment } from '@fitness-force/environments';
import { AppErrors, NotFoundError } from '@fitness-force/models';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CommunicationCategory,
  CommunicationCategoryApiResponse,
  CreateCommunicationCategoryRequest,
  ListCommunicationCategoriesApiResponse,
  UpdateCommunicationCategoryRequest,
  mapCommunicationCategoryFromApi,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class CommunicationCategoryService {
  private readonly http = inject(HttpClient);

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  private categoryUrl(categoryId: number, path: string): string {
    return (
      environment.BASE_URL +
      path.replace('{communication_category_id}', categoryId.toString())
    );
  }

  listCategories(): Observable<CommunicationCategory[]> {
    return this.http
      .get<ListCommunicationCategoriesApiResponse | CommunicationCategoryApiResponse[]>(
        environment.BASE_URL + CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.LIST,
        { headers: this.httpOptions.headers },
      )
      .pipe(
        map((res) => this.normalizeListResponse(res)),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  getCategoryById(categoryId: number): Observable<CommunicationCategory> {
    return this.http
      .get<CommunicationCategoryApiResponse>(
        this.categoryUrl(
          categoryId,
          CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.GET_BY_ID,
        ),
        { headers: this.httpOptions.headers },
      )
      .pipe(
        map((res) => mapCommunicationCategoryFromApi(res)),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  createCategory(
    body: CreateCommunicationCategoryRequest,
  ): Observable<CommunicationCategory> {
    return this.http
      .post<CommunicationCategoryApiResponse>(
        environment.BASE_URL + CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.CREATE,
        body,
        this.httpOptions,
      )
      .pipe(
        map((res) => mapCommunicationCategoryFromApi(res)),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  updateCategory(
    categoryId: number,
    body: UpdateCommunicationCategoryRequest,
  ): Observable<void> {
    return this.http
      .put<void>(
        this.categoryUrl(
          categoryId,
          CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.UPDATE,
        ),
        body,
        this.httpOptions,
      )
      .pipe(
        map(() => undefined),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  deleteCategory(categoryId: number): Observable<void> {
    return this.http
      .delete<void>(
        this.categoryUrl(
          categoryId,
          CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.DELETE,
        ),
        this.httpOptions,
      )
      .pipe(
        map(() => undefined),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  markActive(categoryId: number): Observable<void> {
    return this.http
      .post<void>(
        this.categoryUrl(
          categoryId,
          CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.MARK_ACTIVE,
        ),
        {},
        this.httpOptions,
      )
      .pipe(
        map(() => undefined),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  markInactive(categoryId: number): Observable<void> {
    return this.http
      .post<void>(
        this.categoryUrl(
          categoryId,
          CONST_URL_SEGEMENTS.COMMUNICATION_CATEGORIES.MARK_INACTIVE,
        ),
        {},
        this.httpOptions,
      )
      .pipe(
        map(() => undefined),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  private normalizeListResponse(
    res: ListCommunicationCategoriesApiResponse | CommunicationCategoryApiResponse[],
  ): CommunicationCategory[] {
    const items = Array.isArray(res) ? res : (res.items ?? []);
    return items.map((item) => mapCommunicationCategoryFromApi(item));
  }

  private handleError(error: Response): Observable<never> {
    if (error.status === 404) {
      return throwError(() => new NotFoundError(error));
    }
    return throwError(() => new AppErrors(error));
  }
}
